// Detects a tenant slug/subdomain from the current hostname generically, e.g.
// "tenant1.tcpfund.com" -> "tenant1", "tcpfund.com" -> null (bare domain, no
// subdomain -> default theme), "test-local.localhost" -> "test-local",
// plain "localhost" -> null. Works for any real domain without needing the
// production domain hardcoded/confirmed in advance — a hostname counts as
// having a subdomain when it has more labels than its base domain needs
// (2 for a normal domain like "tcpfund.com", 1 for the special "localhost").
export function getTenantSubdomain(hostname = window.location.hostname) {
  if (!hostname) return null;

  const labels = hostname.split(".");

  // "localhost" and "sub.localhost" — no real TLD, base domain is 1 label.
  if (labels[labels.length - 1] === "localhost") {
    if (labels.length <= 1) return null; // bare "localhost"
    return labels.slice(0, -1).join(".");
  }

  // Plain IP addresses (e.g. 127.0.0.1) never carry a tenant subdomain.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;

  // Normal domain: base domain is the last 2 labels (e.g. "tcpfund.com").
  // Anything before that is the subdomain portion.
  if (labels.length <= 2) return null; // bare "tcpfund.com"
  return labels.slice(0, -2).join(".");
}

// Base domain = hostname with the current subdomain (if any) stripped, using
// the same label-counting rule as getTenantSubdomain above so both stay in
// sync for localhost/IP/real-domain cases without a separate env var.
function getBaseDomain(hostname = window.location.hostname) {
  const labels = hostname.split(".");

  if (labels[labels.length - 1] === "localhost") return "localhost";
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return hostname;
  if (labels.length <= 2) return hostname;
  return labels.slice(-2).join(".");
}

// Builds a full URL on a different tenant's subdomain, preserving protocol
// and port (dev servers commonly run on a non-default port). Used when a
// user picks a tenant other than the one implied by the current subdomain —
// since tenant identity is subdomain-derived app-wide, staying on the old
// subdomain after switching would leave theming/context mismatched.
export function buildTenantUrl(targetSubdomain, path = "/") {
  const { protocol, hostname, port } = window.location;
  const baseDomain = getBaseDomain(hostname);
  const host = targetSubdomain ? `${targetSubdomain}.${baseDomain}` : baseDomain;
  const portSuffix = port ? `:${port}` : "";
  return `${protocol}//${host}${portSuffix}${path}`;
}

function useTenantFromSubdomain() {
  return getTenantSubdomain(window.location.hostname);
}

export default useTenantFromSubdomain;
